import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import FlowDiagram from "@/components/FlowDiagram";
import ClosingCta from "@/components/sections/ClosingCta";
import { INDUSTRIES, getIndustry } from "@/config/industries";

export function IndustriesPage() {
  return (
    <main data-testid="industries-page">
      <Seo
        title="Industries"
        description="AI sales workflows for B2B SaaS, clinics, education and fitness businesses in Singapore. Pick your industry to see what we build."
        path="/industries"
      />
      <PageHero
        label="Industries"
        headline="Built for how your industry actually sells."
        subline="The gaps look different in a SaaS pipeline and a clinic inbox. Pick yours to see the workflows we build most often."
      />
      <section className="px-6 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-5">
          {INDUSTRIES.map((ind) => (
            <Link
              key={ind.slug}
              to={`/industries/${ind.slug}`}
              className="card-lift bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 block"
              data-testid={`industry-card-${ind.slug}`}
            >
              <h2 className="font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C]">{ind.name}</h2>
              <p className="mt-3 text-sm text-[#4A463F] leading-relaxed">{ind.headline}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A]">
                See the workflows <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <ClosingCta testId="industries-closing-cta" />
    </main>
  );
}

export default function IndustryPage() {
  const { slug } = useParams();
  const industry = getIndustry(slug);

  if (!industry) return <Navigate to="/industries" replace />;

  return (
    <main data-testid={`industry-page-${industry.slug}`}>
      <Seo
        title={industry.headline}
        description={`${industry.headline} AI sales workflows for ${industry.name.toLowerCase()} in Singapore, built by Raki Solutions.`}
        path={`/industries/${industry.slug}`}
      />
      <PageHero label={industry.name} headline={industry.headline} />

      <section className="px-6 sm:px-8 pb-20 sm:pb-28" data-testid="industry-workflows">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C]">
            Workflows we build
          </h2>
          <div className="mt-10 space-y-5">
            {industry.workflows.map((w, i) => (
              <div
                key={w.title}
                className="bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 sm:p-8"
                data-testid={`industry-workflow-${i + 1}`}
              >
                <h3 className="font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C] mb-6">{w.title}</h3>
                <FlowDiagram steps={w.steps} testId={`industry-workflow-${i + 1}-diagram`} compact />
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-[#4A463F]">
            <Link to="/workflows" className="font-semibold text-[#C8703A] underline underline-offset-4" data-testid="industry-library-link">
              Browse the full workflow library
            </Link>
          </p>
        </div>
      </section>

      <ClosingCta testId="industry-closing-cta" />
    </main>
  );
}
